import { SignInUrlBuilder } from '@tableau/shared-urls';
import { setConfig, SharedWidgetsConfig } from '@tableau/widgets';
import * as _ from 'underscore';

import { setRootAdminUiState } from '../app/actions/spaces/RootAdminUiStateActions';
import { setNavLocation } from '../app/actions/spaces/SetNavLocationAction';
import { setPreviousUrl } from '../app/actions/spaces/SetPreviousUrlReducerAction';
import { appStore } from '../app/AppStore';
import { LoginUrlsWrapper } from '../app/spaces/Login/LoginUrls';
import { UrlParamKeys } from '../app/UrlParamKeys';
import { GlobalShortcutService } from '../common/utilities/GlobalShortcuts';
import { Tableau } from '../common/utilities/TableauLog';
import { UserAgentSupport } from '../common/utilities/UserAgentSupport';
import { FeatureFlags } from './FeatureFlags';
import { CommonErrorCode, UninitializedServerErrorCode } from './helpers/ErrorCodes';
import { Helpers } from './helpers/Helpers';
import { getSiteUrlNameIfOnSigninPath, isVizUrl, shouldResetPreviousUrl, shouldSetPreviousUrl } from './helpers/UrlHelpers';
import { ImagePreloader } from './ImagePreloader';
import { L10n } from './L10n/L10n';
import { AuthenticationType } from './ServerApi/types/IServerSettingsUnauthenticated';
import { BrowserTitleService } from './services/BrowserTitleService';
import { ErrorState } from './services/ErrorState';
import { ModalService } from './services/ModalService';
import { ReactModalService } from './services/ReactModalService';
import { AuthLevel, ServerService } from './services/ServerService';
import { SessionService } from './services/SessionService';
import { SiteSwitchService } from './services/SiteSwitchService';
import { StateWrapperService } from './services/StateWrapperService';
import { TemplatePreloadService } from './services/TemplatePreloadService';
import { ToasterService } from './services/ToasterService';
import { TopBarService } from './services/TopBarService';
import { VizService } from './services/VizService';
import { WindowLocationService } from './services/WindowLocationService';
import { WindowPopupService } from './services/WindowPopupService';

/**
 * This is the run block for VizPortal. It is run once when the app is loaded.
 * We declare it in an isolated module for the reasons mentioned in Angular's
 * documentation (https://docs.angularjs.org/guide/module#run-blocks):
 *
 *    Run blocks are the closest thing in Angular to the main method.
 *    A run block is the code which needs to run to kickstart the application.
 *    It is executed after all of the service have been configured and the
 *    injector has been created. Run blocks typically contain code which is
 *    hard to unit-test, and for this reason should be declared in isolated
 *    modules, so that they can be ignored in the unit-tests.
 */
angular.module('VizPortalRun', ['VizPortal']).run(<any[]>[
  '$rootScope',
  '$window',
  '$document',
  '$interval',
  '$location',
  '$urlRouter',
  '$timeout',
  '$translate',
  'ModalService',
  'ServerService',
  'SessionService',
  'SiteSwitchService',
  'StateWrapperService',
  'ToasterService',
  'TopBarService',
  'WindowLocationService',
  'VizService',
  'WindowPopupService',
  'BrowserTitleService',
  'TemplatePreloadService',
  'ErrorState',
  'GlobalShortcutService',
  (
    $rootScope: ng.IRootScopeService,
    $window: ng.IWindowService,
    $document: ng.IDocumentService,
    $interval: ng.IIntervalService,
    $location: ng.ILocationService,
    $urlRouter: ng.ui.IUrlRouterService,
    $timeout: ng.ITimeoutService,
    $translate: ng.translate.ITranslateService,
    modalService: ModalService,
    server: ServerService,
    SessionService: SessionService,
    SiteSwitchService: SiteSwitchService,
    StateWrapperService: StateWrapperService,
    toaster: ToasterService,
    TopBarService: TopBarService,
    WindowLocationService: WindowLocationService,
    VizService: VizService,
    WindowPopupService: WindowPopupService,
    BrowserTitleService: BrowserTitleService,
    TemplatePreloadService: TemplatePreloadService,
    errorState: ErrorState,
    globalShortcuts: GlobalShortcutService,
  ) => {
    // Defer prefetching until after the first stateChangeSuccess so
    // we don't do any extra work before the page is first rendered
    const removeImagePreloadListener = $rootScope.$on('$stateChangeSuccess', () => {
      $timeout(() => ImagePreloader.preloadDynamicIconImages(), 1000);
      removeImagePreloadListener();
    });

    const removeTemplatePreloadListener = $rootScope.$on('$stateChangeSuccess', () => {
      $timeout(() => TemplatePreloadService.preloadTemplates(), 1000);
      removeTemplatePreloadListener();
    });

    function redirectToLogin(errorCode: number) {
      const params: SignInUrlBuilder.QueryParams = {
        redirect: $location.url() || null,
        error: errorCode === CommonErrorCode.InvalidSession ? null : errorCode
      };
      if (server.isAuthenticationAutoLogin()) {
        // The signin state will attempt to auto-login by default. We don't want it to here.
        params.disableAutoSignin = true;
      }
      const siteUrlName = getSiteUrlNameIfOnSigninPath($location.path(), $location.search());
      if (siteUrlName) {
        params.site = siteUrlName;
      }
      LoginUrlsWrapper.navigateToSignIn(params);
    }

    function shouldRedirectToLogin(nextState: ng.ui.IState, nextUrl: string) {
      if (!nextState) {
        return false;
      }
      const isNextStateAnonymous = nextState.data && nextState.data.authLevel === AuthLevel.Anonymous;
      const isNonVizPageWithRestrictedSession =
        server.isSessionRestrictedTrustedTicket() && nextUrl && !isVizUrl(nextUrl);
      return !isNextStateAnonymous && (!server.inSession() || isNonVizPageWithRestrictedSession);
    }

    function shouldAutoLogin(nextState: ng.ui.IState) {
      const isNextStateAnonymous = nextState && nextState.data && nextState.data.authLevel === AuthLevel.Anonymous;
      return !server.inSession() && !isNextStateAnonymous && server.isAuthenticationAutoLogin();
    }

    function shouldRedirectToEmbeddedViz(nextUrl: string) {
      const isPhone = Math.min(screen.width, screen.height) <= server.dsdPhoneSize();

      if (nextUrl && isVizUrl(nextUrl)) {
        return VizService.isEmbedParamOn() || server.isSessionRestrictedTrustedTicket() || isPhone;
      }

      return false;
    }

    function shouldRedirectToGuestViz(nextUrl: string) {
      return nextUrl && isVizUrl(nextUrl) && !server.inSession() && server.isGuestEnabled();
    }

    function shouldRedirectToVizExport(nextUrl: string) {
      return nextUrl && isVizUrl(nextUrl) && VizService.pathHasExportableFileType();
    }

    function shouldToggleTopBar(nextState: ng.ui.IState) {
      const collapsedAllowed = !!(nextState && nextState.data && nextState.data.allowCollapsedTopBar);
      return TopBarService.isCollapsed() && !collapsedAllowed;
    }


    $rootScope.$on('$stateChangeSuccess', (
      event: ng.IAngularEvent,
      toState: ng.ui.IState,
      toStateParams: any) =>
    {
      const authLevel = toState.data ? toState.data.authLevel : AuthLevel.Anonymous;
      if (!server.isUserAuthorized(authLevel, '', '')) {
        if (server.inSession()) {
          errorState.go({
            redirectSiteUrlName: toStateParams.siteUrlName // This may not be the current site
          });
        } else {
          LoginUrlsWrapper.navigateToSignIn({});
        }
      }

      if (shouldToggleTopBar(toState)) {
        TopBarService.toggle();
      }
    });

    $rootScope.$on('$stateChangeError', function(event, toState, toParams, fromState, fromParams, error) {
      Tableau.Logger().error(`Error [${error.message}] caught during state change from ${fromState.name}`
        + `to ${toState.name}`);
    });

    $rootScope.$on('$stateChangeStart', (
      event: ng.IAngularEvent,
      toState: ng.ui.IState,
      toStateParams: any,
      fromState: ng.ui.IState,
      fromStateParams: any) =>
    {
      // Reset the title to just the server name.
      BrowserTitleService.setTitleToServerName();

      // If we are moving to a site area
      if (_.isString(toStateParams.siteUrlName)) {
        // switchIfNeeded() handles site-switch error routing
        // $location.url() is used in redirectToOtherPod to figure out the destination URL. This works well
        // in the case where the user changes the site portion of the URL by editing the address bar.
        // However, this does not work in the case when this code is called when a server admin clicks on a
        // site-name in the sites list. Matt York has agreed to fix this when he replaces UIRouter.
        SiteSwitchService.switchIfNeeded(toStateParams.siteUrlName, '/#' + $location.url());
      }

      if (shouldResetPreviousUrl({toState, toStateParams, fromState, fromStateParams, currentPath: $location.path()})) {
        // We make the previous url blank when we don't want to keep it.
        appStore.dispatch(setPreviousUrl(''));
      }

    });


    $rootScope.$on('$stateChangeSuccess', (
      event: ng.IAngularEvent,
      toState: ng.ui.IState,
      toParams: any,
      fromState: ng.ui.IState) =>
    {
      appStore.dispatch(setNavLocation(toState && toState.name, toParams));
    });

    function maybeVizRedirect(nextUrl: string, shouldRedirectWithoutAddingToHistory: boolean, event?: ng.IAngularEvent) {
      if (shouldRedirectToVizExport(nextUrl)) {
        WindowLocationService.navigateToWithoutAddingToHistory(VizService.getVizqlUrl());
        return true;
      } else if (shouldRedirectToEmbeddedViz(nextUrl)) {
        VizService.redirectToEmbeddedMode(shouldRedirectWithoutAddingToHistory, event, {shouldPreventDefault: true});
        return true;
      } else if (shouldRedirectToGuestViz(nextUrl)) {
        VizService.redirectToEmbeddedMode(shouldRedirectWithoutAddingToHistory, event, {isFromGuest: true});
        return true;
      }
      return false;
    }

    function shouldRedirectToSitePicker(props: {isSuccessfulSamlLogin: boolean}) {
      return props.isSuccessfulSamlLogin && server.isSessionMultiSite() && isRootUrl();
    }

    function isRootUrl() {
      return $location.path() === '' || $location.path() === '/';
    }

    // VizPortal 9.0 Session Expiration Solution - B144748
    // 1) Only the getActionInfo and getSessionInfo requests will extend your session
    // 2) As long as the user is “interacting ”, getSessionInfo will be requested once every 5 minutes or half
    //    the configured idle time limit (wgserver.session.idle_limit), whichever is less
    // 3) We determine if the user is “interacting” by listening to mousemove and keydown events in the browser
    function initializeSessionPing() {
      // If the browser tab comes into focus we want to refresh the session info.
      // Do this at most once every 15 seconds
      $($window).focus(_.throttle(() => server.refreshSessionInfo(true), 15000, {trailing: false}));

      const fiveMinutesInMilliseconds = 5 * 60 * 1000;
      const halfSessionIdleLimitInMilliseconds = server.getSessionIdleLimitSeconds() * 1000;
      let sessionPingPeriodWhileUserIsInteracting = Math.min(fiveMinutesInMilliseconds, halfSessionIdleLimitInMilliseconds);

      // Don't let the period fall below 15 seconds, because that's absurdly short
      sessionPingPeriodWhileUserIsInteracting = Math.max(sessionPingPeriodWhileUserIsInteracting, 15 * 1000);

      const listenForInteractionAfterSessionPingPeriod = () => {
        // timeout for session ping period, then listen to interaction events
        $interval(() => {
          // $.one will bind to the event once per element per event type. In this case the handler will
          // be fired twice if mousemove and keydown both fire, so ensure the handler is only called once
          $($document).one('keydown mousemove touchstart', _.once(() => {
            // User is alive! ping the server, then sleep on it
            server.refreshSessionInfo(true);
            listenForInteractionAfterSessionPingPeriod();
          }));
        }, sessionPingPeriodWhileUserIsInteracting, 1, false);
      };

      // Kick it off
      listenForInteractionAfterSessionPingPeriod();
    }

    if (FeatureFlags.keyboardShortcutsEnabled){
      globalShortcuts.setupGlobalShortcuts();
    }

    // The client is hidden initially. We should show it once we know we will be displaying UI
    // (as opposed to navigating away from vizportal client).
    function showClient() {
      $($window.document.body).show();
      // When the VizClient puts an element into fullscreen mode, Edge (for unknown reasons) retains the
      // VizPortal top bar. We work around that here by hiding the top bar in fullscreen mode. TFS 557632
      if (UserAgentSupport.isEdge()) {
        // TODO: Remove the "any" type assertion after https://github.com/Microsoft/TSJS-lib-generator/pull/597 is merged
        $($window.document).on('fullscreenchange', () => { $('.tb-top-bar').toggle(($window.document as any).fullscreenElement === null); });
      }
    }

    // Overrides the server authentication type with the uppercased "auth" param from the url
    // Example: http://localhost/#/signin?auth=external -> EXTERNAL
    // TFS:373719 Reimplement the allow_direct_access scenario for 9.0
    function overrideAuthenticationTypeBasedOnUrl() {
      const override: AuthenticationType = $location.search()[UrlParamKeys.AuthenticationTypeOverride];
      $location.search(UrlParamKeys.AuthenticationTypeOverride, null);
      const newAuthType = override ? override.toUpperCase() as AuthenticationType : null;
      server.overrideAuthenticationType(newAuthType);
    }

    function bootstrapCheckInitialAdmin(code: number, nextState: ng.ui.IState, nextUrl: string) {
      // Defect 458853:[LC] Intermittent Calls to getSessionInfo clears workgroup_session_id, which makes data source authentication imposible
      // When user not authenticated on Tableau Server, data source authentication dialog
      // will get temporary guest session in order to client to encrypt credentials and server to descrypt them
      // But calls to getSessionInfo will clear session cookie. Disabling calls to getSessionInfo when requested.
      const stateParams = StateWrapperService.getStateParams($location.path(), $location.search());
      if (stateParams && !stateParams.hasOwnProperty('disableSessionPing')){
        // Initializing sesssion ping only when LazyConnections disabled or disableSessionPing was NOT explicitly requested in state params
        initializeSessionPing();
      }

      overrideAuthenticationTypeBasedOnUrl();
      // Need to know if the user already authenticated or not
      if (code === UninitializedServerErrorCode.UntrustedMachine) {
        appStore.dispatch(setRootAdminUiState('untrusted'));
        showClient();
      } else if (code === UninitializedServerErrorCode.ReadyToInitialize) {
        appStore.dispatch(setRootAdminUiState('init'));
        showClient();
      } else {
        bootstrapCheckAutoLogin(code, nextState, nextUrl);
      }

    }

    function bootstrapCheckAutoLogin(code: number, nextState: ng.ui.IState, nextUrl: string) {
      if (shouldAutoLogin(nextState)) {
        // Go to interstitial page that may be showing 'Signing In' spinner
        appStore.dispatch(setRootAdminUiState('interstitial'));
        _.defer(() => showClient());
        server.autoLogin().then(() => {
          appStore.dispatch(setRootAdminUiState('default'));

          // Go to site picker if destination site is ambiguous
          if (isRootUrl() && server.isSessionMultiSite()) {
            LoginUrlsWrapper.navigateToLoginSitePicker();
          } else {
            bootstrapCheckRedirects(code, nextState, nextUrl);
          }
        }, (errorCode: number) => {
          appStore.dispatch(setRootAdminUiState('default'));
          bootstrapCheckRedirects(errorCode, nextState, nextUrl);
        });
      } else {
        bootstrapCheckRedirects(code, nextState, nextUrl);
      }
    }

    function bootstrapCheckRedirects(code: number, nextState: ng.ui.IState, nextUrl: string) {
      if (maybeVizRedirect(nextUrl, true)) {
        // maybeVizRedirect handles the redirect
      } else if (shouldRedirectToLogin(nextState, nextUrl)) {
        showClient();
        redirectToLogin(code);
      } else if (shouldRedirectToSitePicker({
          isSuccessfulSamlLogin: Helpers.BooleanQueryParamRegex.test($location.search()[UrlParamKeys.IsFromSaml])
        })) {
        showClient();
        LoginUrlsWrapper.navigateToLoginSitePicker();
      } else {
        // Retry $locationChange
        showClient();
        $urlRouter.sync();
      }
    }

    $($window).on('load', () => {
      // Record page load time if the browser supports the Navigation Timing API
      // see: http://caniuse.com/#feat=nav-timing

      // Using $timeout to defer execution until after window.onload so that:
      // 1) we aren't blocking onload 2) we have the loadEventEnd stat from the Navigation Timing API
      $timeout(() => {
        // 'performance' may be null if the browser does not support the Navigation Timing API
        // 'timing' may be null if the browser user has disabled this feature
        //         (as per http://www.w3.org/TR/navigation-timing/#processing-model)
        if ($window.performance && $window.performance.timing) {
          server.recordNavigationTiming($window.performance.timing);
        }
      });
    });

    function editingDeviceSpecificAlert() {
      return UrlParamKeys.DeviceType in $location.search() && UrlParamKeys.EditDataAlert in $location.search();
    }

    $rootScope.$on('$locationChangeStart', (event: ng.IAngularEvent, newUrl: string, oldUrl: string) => {
      const nextState = StateWrapperService.getMatchingState($location.path(), $location.search());
      const nextUrl = window.location.href;  // use me instead of nextState when possible

      if (SessionService.isSessionStateUnknown()) {
        // We've just loaded the app and session state is unknown. We can't decide what
        // to do until we know the session state, so prevent the location change. This
        // will prevent the app from entering the ui router state machine.
        event.preventDefault();
        server.bootstrapSession().then((code: number) => bootstrapCheckInitialAdmin(code, nextState, nextUrl));
      } else if(maybeVizRedirect(nextUrl, VizService.shouldRedirectWithoutAddingToHistory(oldUrl), event) && (!editingDeviceSpecificAlert())) {
        event.preventDefault();
      } else if (shouldRedirectToLogin(nextState, nextUrl)) {
        event.preventDefault();
        redirectToLogin(null);
      }
    });

    function isOnSameViewPath(newUrl: string, oldUrl: string): boolean {
      const newPath = VizService.getPath(newUrl);
      const oldPath = VizService.getPath(oldUrl);
      if (newPath === null || oldPath === null) {
        return false;
      }
      return (newPath === oldPath);
    }

    // Dismiss modal dialogs on location change e.g. if the user clicks the back button
    // TFS827926 - Lightbox (modal) closes when the InstanceId is changed after load.
    // Modals should only close when we're going to a different page.
    // AFAIK, the only place the $location search properties are changed is in viewing a viz.
    $rootScope.$on('$locationChangeStart', (event: ng.IAngularEvent, newUrl: string, oldUrl: string) => {
      if (!isOnSameViewPath(newUrl, oldUrl)) {
        modalService.dismissAll('location change');
        ReactModalService.dismissAll('location change');
      }
    });

    $rootScope.$on('$locationChangeStart', (event: ng.IAngularEvent, newUrl: string, oldUrl: string) => {
      if(shouldSetPreviousUrl(oldUrl, newUrl, VizService)) {
        // setting the previous url here keeps the sorts and filter parameters in the url
        appStore.dispatch(setPreviousUrl(oldUrl));
      }
    });

    const sharedWidgetsOverrideStyles = {
      // Legacy Vizportal dialogs use black glass with 0.2 opacity; see app.styl
      Colors: {
        OpaqueGlass: 'rgb(0, 0, 0)'
      },
      Opacity: {
        OpaqueGlass: 0.2
      }
    };

    // The default root container for the shared widgets needs to be inside the Angular app, because that's
    // where everything inherits from in our CSS.
    const sharedWidgetsConfig: SharedWidgetsConfig = {
      defaultStackingRootId: 'ng-app',
      styles: sharedWidgetsOverrideStyles
    };
    setConfig(sharedWidgetsConfig);

    // Some very basic error handling
    $rootScope.$on('$stateChangeError', (event: ng.IAngularEvent,
                                         toState: ng.ui.IState) => {

      event.preventDefault(); // prevents the failed state change from rolling back the url to the last known good state.

      if (toState.data['errorState']) {
        errorState.go(toState.data['errorState']);
      } else {
        errorState.go();
      }
    });

    // Keep html lang attribute synchronized with the current language
    $rootScope.$watch(() => L10n.language, () => {
      if (L10n.language) {
        angular.element('html')
          .attr('lang', L10n.language)
          .attr('xml:lang', L10n.language);
      }
    });
  }
]);
